// components/adhesion/MembershipLevelSelector.tsx
import React, { useState } from 'react';
import { Users, User, HelpCircle, ArrowRight, Check, Info, CheckCircle, XCircle } from 'lucide-react';
import { MembershipLevel, NipStatus } from '../../types/adhesion.types';

interface MembershipLevelSelectorProps {
    onLevelSelected: (level: MembershipLevel, nipStatus: NipStatus) => void;
    className?: string;
}

const MembershipLevelSelector: React.FC<MembershipLevelSelectorProps> = ({ onLevelSelected, className = '' }) => {
    const [step, setStep] = useState(1);
    const [hasNip, setHasNip] = useState<boolean | null>(null);
    const [selectedNipAnswer, setSelectedNipAnswer] = useState<boolean | null>(null);
    const [selectedLevel, setSelectedLevel] = useState<MembershipLevel | null>(null);
    const [showFAQ, setShowFAQ] = useState(false);

    const handleNipClick = (hasNip: boolean) => {
        setSelectedNipAnswer(hasNip);
    };

    const handleNipContinue = () => {
        if (selectedNipAnswer !== null) {
            setHasNip(selectedNipAnswer);
            if (selectedNipAnswer) {
                // Avec NIP, direction directe vers membre complet
                onLevelSelected('full_member', 'has_nip');
            } else {
                // Sans NIP, aller à l'étape de choix
                setStep(2);
            }
        }
    };

    const handleLevelClick = (level: MembershipLevel) => {
        setSelectedLevel(level);
    };

    const handleContinue = () => {
        if (selectedLevel) {
            let nipStatus: NipStatus;
            
            if (hasNip === true) {
                nipStatus = 'has_nip';
            } else if (selectedLevel === 'partial_member') {
                nipStatus = 'no_nip_will_get';
            } else {
                nipStatus = 'no_nip_wont_get';
            }
            
            onLevelSelected(selectedLevel, nipStatus);
        }
    };


    const getAvailableLevels = () => {
        // Sans NIP, choix entre sympathisant et partiel (qui compte obtenir un NIP)
        return [
            {
                level: 'sympathisant' as MembershipLevel,
                title: 'Sympathisant',
                description: 'Soutenir le mouvement sans engagement majeur',
                icon: <Users className="w-6 h-6" />,
                benefits: ['Newsletter et actualités', 'Invitations aux événements', 'Communauté en ligne', 'Code de parrainage personnel']
            },
            {
                level: 'partial_member' as MembershipLevel,
                title: 'Membre Partiel',
                description: 'Engagement plus fort en attendant votre NIP',
                icon: <User className="w-6 h-6" />,
                benefits: ['Tous avantages sympathisant', 'Espace membre privé', 'Documents officiels', 'Upgrade automatique avec NIP'],
                recommended: true,
                highlight: 'Upgrade automatique avec NIP'
            }
        ];
    };

    return (
        <div className={`max-w-4xl mx-auto p-6 ${className}`}>
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                
                {/* Étape 1: Question sur le NIP */}
                {step === 1 && (
                    <div className="p-8">

                        <div className="text-center mb-8">
                            <h3 id="nip-question" className="text-2xl font-bold text-gray-900 mb-3">
                                Avez-vous un NIP ?
                            </h3>
                            <p className="text-gray-600">
                                Le NIP est votre Numéro d'Identification Personnelle sur votre CNI
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto" role="radiogroup" aria-labelledby="nip-question">
                            <button
                                onClick={() => handleNipClick(true)}
                                role="radio"
                                aria-checked={selectedNipAnswer === true}
                                aria-describedby="nip-yes-description"
                                className={`
                                    group p-8 border rounded-lg transition-all duration-300 hover:-translate-y-1 focus:ring-4 focus:ring-green-200 focus:outline-none
                                    ${selectedNipAnswer === true 
                                        ? 'bg-green-50 border-green-400 shadow-lg' 
                                        : 'bg-white border-gray-200 hover:border-green-300 hover:shadow-xl'
                                    }
                                `}
                            >
                                <div className="flex items-center justify-center mb-6">
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                                        selectedNipAnswer === true 
                                            ? 'bg-green-200' 
                                            : 'bg-green-100 group-hover:bg-green-200'
                                    }`}>
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    </div>
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-3">Oui, j'ai un NIP</h4>
                                <p id="nip-yes-description" className="text-gray-600 leading-relaxed">
                                    Je possède ma CNI avec mon numéro NIP
                                </p>
                            </button>

                            <button
                                onClick={() => handleNipClick(false)}
                                role="radio"
                                aria-checked={selectedNipAnswer === false}
                                aria-describedby="nip-no-description"
                                className={`
                                    group p-8 border rounded-lg transition-all duration-300 hover:-translate-y-1 focus:ring-4 focus:ring-orange-200 focus:outline-none
                                    ${selectedNipAnswer === false 
                                        ? 'bg-orange-50 border-orange-400 shadow-lg' 
                                        : 'bg-white border-gray-200 hover:border-orange-300 hover:shadow-xl'
                                    }
                                `}
                            >
                                <div className="flex items-center justify-center mb-6">
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                                        selectedNipAnswer === false 
                                            ? 'bg-orange-200' 
                                            : 'bg-orange-100 group-hover:bg-orange-200'
                                    }`}>
                                        <XCircle className="w-8 h-8 text-orange-600" />
                                    </div>
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-3">Non, je n'ai pas de NIP</h4>
                                <p id="nip-no-description" className="text-gray-600 leading-relaxed">
                                    Je n'ai pas encore ma CNI ou je vis à l'étranger
                                </p>
                            </button>
                        </div>

                        {/* Bouton Suivant pour la question NIP */}
                        {selectedNipAnswer !== null && (
                            <div className="mt-8 text-center">
                                <button
                                    onClick={handleNipContinue}
                                    aria-live="polite"
                                    className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 focus:outline-none transition-colors"
                                >
                                    {selectedNipAnswer ? 'Continuer (Membre Complet)' : 'Continuer (Choisir le niveau)'}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </button>
                            </div>
                        )}

                        {/* FAQ Toggle */}
                        <div className="bg-gray-50 rounded-xl p-4">
                            <button
                                onClick={() => setShowFAQ(!showFAQ)}
                                className="flex items-center justify-between w-full text-left"
                            >
                                <span className="text-sm font-semibold text-gray-900 flex items-center">
                                    <HelpCircle className="w-4 h-4 mr-2 text-blue-600" />
                                    Qu'est-ce que le NIP exactement ?
                                </span>
                                <ArrowRight className={`w-4 h-4 text-gray-400 transition-transform ${showFAQ ? 'rotate-90' : ''}`} />
                            </button>

                            {showFAQ && (
                                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 text-sm text-gray-700">
                                    <p>
                                        <strong>Le NIP (Numéro d'Identification Personnelle)</strong> est l'identifiant unique de chaque citoyen,
                                        inscrit sur la Carte Nationale d'Identité (CNI).
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Étape 2: Choix du niveau d'adhésion */}
                {step === 2 && (
                    <div className="p-8">
                        <div className="text-center mb-8">
                            <button
                                onClick={() => setStep(1)}
                                className="text-sm text-gray-600 hover:text-gray-800 mb-6 flex items-center mx-auto"
                            >
                                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                                Modifier ma réponse
                            </button>
                            
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                Choisissez votre niveau d'adhésion
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                            {getAvailableLevels().map((option) => (
                                <button
                                    key={option.level}
                                    className={`
                                        group p-8 border rounded-lg transition-all duration-300 hover:-translate-y-1 text-left
                                        ${selectedLevel === option.level
                                            ? 'bg-blue-50 border-blue-400 shadow-lg'
                                            : 'bg-white border-gray-200 hover:shadow-xl hover:border-gray-300'
                                        }
                                    `}
                                    onClick={() => handleLevelClick(option.level)}
                                >
                                    {option.recommended && (
                                        <div className="absolute -top-3 left-6 px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                                            Recommandé
                                        </div>
                                    )}

                                    <div className="flex items-center mb-4">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${option.recommended ? 'bg-blue-100' : 'bg-gray-100'} group-hover:scale-110 transition-transform`}>
                                            <div className={option.recommended ? 'text-blue-600' : 'text-gray-600'}>
                                                {option.icon}
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {option.title}
                                        </h3>
                                    </div>

                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        {option.description}
                                    </p>

                                    <div className="space-y-2">
                                        {option.benefits.slice(0, 3).map((benefit, index) => (
                                            <div key={index} className="flex items-center text-sm text-gray-600">
                                                <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                                                {benefit}
                                            </div>
                                        ))}
                                        {option.benefits.length > 3 && (
                                            <div className="text-sm text-gray-500 mt-2">
                                                +{option.benefits.length - 3} autres avantages
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Bouton Suivant */}
                        {selectedLevel && (
                            <div className="mt-8 text-center">
                                <button
                                    onClick={handleContinue}
                                    className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                                >
                                    Continuer avec {getAvailableLevels().find(o => o.level === selectedLevel)?.title}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Note légale en bas */}
                <div className="bg-gray-50 p-4 border-t border-gray-200">
                    <div className="flex items-start">
                        <Info className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-600">
                            Conformément à la loi, seuls les citoyens avec NIP peuvent être membres complets d'un parti politique.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MembershipLevelSelector;